import { useCallback, useState } from 'react';
import { useAtomValue } from 'jotai';

import request from 'lib/request';
import { useServerExport, type ExportParams } from 'lib/hooks/useServerExport';
import { exportErrorMessage } from 'lib/hooks/use-spatial-export';
import { sessionResourceAtom } from 'atoms/root';

// What a results section exports: its dataset's search endpoint and label, the
// last searched payload, and (table view) the visible column ids in display order.
export type MapQueryExportParams = {
  endpoint: string;
  label: string;
  payload?: Record<string, unknown>;
  columns?: string[];
};

// One CSV export at a time for the whole page. Called by the always-mounted
// page so a running export survives tab switches and hiding the results table.
export const useMapQueryExport = () => {
  const userId = useAtomValue(sessionResourceAtom)?.id;
  // Which dataset the running (or last) export belongs to, for the banner. Set
  // before the status leaves idle, so the banner never renders without it.
  const [exportLabel, setExportLabel] = useState('');

  // Stable: everything request-specific arrives through startExport's params.
  const exportFn = useCallback(async (params?: ExportParams) => {
    const { endpoint, payload, columns } = params as MapQueryExportParams;
    const body = columns ? { ...payload, visible_columns: columns } : payload;
    const response = await request.post<
      { exportId: string },
      { errors?: Array<{ message?: string }>; error?: string }
    >(`${endpoint}/export`, body ?? {}, new AbortController());
    if (response.type !== 'success') {
      // Refusals use the error envelope; a failed enqueue sends a bare `error`. Parsed by the shared
      // exportErrorMessage (also used by the spatial exports) so the two can't drift.
      const failure = response.type === 'error' ? response.data : undefined;
      throw new Error(exportErrorMessage(failure) ?? 'Failed to start export');
    }
    return { exportId: response.data.exportId };
  }, []);

  const { startExport, ...rest } = useServerExport({
    channel: userId ? `user_${userId}_exports` : null,
    event: 'map_query_export_complete',
    exportFn,
  });

  const start = useCallback(
    (params: MapQueryExportParams) => {
      setExportLabel(params.label);
      return startExport(params);
    },
    [startExport]
  );

  return { ...rest, startExport: start, exportLabel, userId };
};

export type MapQueryExport = ReturnType<typeof useMapQueryExport>;
